import { Injectable } from '@angular/core';

import { Subject } from 'rxjs';

import { Event, SubscriptionHandler } from '@mirage-projects/communication-library';
import {
    JobService,
    LogService,
    ModelConverterService,
    NotificationService,
    NotificationTopics,
    TranslationService,
} from '@mirage-projects/cw-commons';
import {
    FileExtended,
    PcvizCRP1ImportJobScanData,
    PcvizCRP1ScanProjectJobAdapter,
    PcvizJobAdapter,
    PcvizProject,
    PcvizScanProcessingOptions,
    PcvizScanWorkflow,
    PcvizUploadConfig,
    UploadIfcImportFileParamJson,
} from '@mirage-projects/cw-models';
import { DbPcvizService, JobPcvizService, WorkflowPcvizService } from '@mirage-projects/cw-pcviz-core';
import { FilesHelperService } from '@mirage-projects/cw-utils';

import { PointSelectorService } from '../../components/create-wizard/point-selector/point-selector.service';
import { WorkspaceService } from '../../components/create-wizard/workspace/workspace.service';
import { CRP1ImportSdService } from '../crp1-import-sd/crp1-import-sd.service';

interface ImportStationJobData {
    processingOptions: any[] | Record<string, any>;
    shouldProcessScans: boolean;
    scans?: PcvizCRP1ImportJobScanData[];
    globalSource?: string;
}

const MAX_PROGRESS = 100 as const;
const HALF_PROGRESS = 50 as const;

/**
 * Service that manages the worflow imports
 */
@Injectable()
export class WorkflowWizardService {
    project!: PcvizProject;
    workflowCore!: PcvizScanWorkflow;
    newDatasetName!: string;
    scanWorkflowJobData: any;
    config!: PcvizUploadConfig;
    resume = false;
    task: any;
    isWorkflowUploading = false;
    workflowScanService: any;

    private closeWizardSubject$ = new Subject<void>();
    closeWizardWorkflow$ = this.closeWizardSubject$.asObservable();

    constructor(
        private jobAdapterService: JobPcvizService,
        private jobService: JobService,
        private filesHelperService: FilesHelperService,
        private dbPcvizService: DbPcvizService,
        private translationService: TranslationService,
        private workflowPcvizService: WorkflowPcvizService,
        private logService: LogService,
        private workspaceService: WorkspaceService,
        private pointSelectorService: PointSelectorService,
        private notificationService: NotificationService,
        private crp1ImportSdService: CRP1ImportSdService,
        private modelConverterService: ModelConverterService
    ) {
        // TODO: We should not get the service like this but from WorkflowRegistrationService, but for that we have to move this class to the cw-workflow library.
        this.workflowScanService = this.workflowPcvizService.getServiceFromCore('ScanWorkflow');
    }

    async initWizard(): Promise<void> {
        if (this.isImportRefs() || this.isImportScan() || this.isImportStation()) {
            this.workflowCore = this.workflowScanService.workflow;

            const workflowCurrentVersion = this.workflowCore.getCurrentVersion();
            const workspace = await this.dbPcvizService
                .getControllerDB('Workspace')
                .getOrCreateWorkspace(this.project, workflowCurrentVersion);

            this.workspaceService.setAddingScans(this.isImportScan());
            this.workspaceService.setAddingStations(this.isImportStation() || this.isCreateStation());
            this.workspaceService.setDatasetNameHidden(this.isCreateStation());

            this.workspaceService.setWorkspace(workspace);
        }
    }

    /**
     * Initializes the configuration object to do the upload
     * @param taskName task name to initialize
     * @param wizardTitle Title of the wizard, to add it to the configuration
     */
    initJobData(taskName: string, wizardTitle: string): PcvizUploadConfig {
        this.isWorkflowUploading = false;
        // TODO: We should not get the service like this but from WorkflowRegistrationService, but for that we have to move this class to the cw-workflow library.
        this.workflowScanService = this.workflowPcvizService.getServiceFromCore('ScanWorkflow');
        this.workflowCore = this.workflowScanService.workflow;
        this.project = this.workflowScanService.pcvizProject;
        this.task = { name: taskName };
        const upload = {
            id: this.dbPcvizService.getUtils().generateUUID(),
        };
        this.scanWorkflowJobData = {
            name: taskName,
            uploadId: upload.id,
            processId: this.resume ? upload.id : undefined,
        };

        this.config = {
            name: `${this.project.getName()} - ${this.translationService.translate(wizardTitle).toLowerCase()}`,
            files: [],
            uploadId: upload.id,
            upload,
        };

        this.initWizard();

        return this.config;
    }

    /**
     * Submit the files into the server
     * @param config Configuration object to do the upload
     */
    async submitProcess(config: PcvizUploadConfig): Promise<void> {
        this.isWorkflowUploading = true;

        this.config = config;
        this.config.object = this.project.getProjectDataset().getProjectObject();

        const jobAdapter = this.jobAdapterService.getJobAdapter();
        if (!jobAdapter) {
            return;
        }

        if (this.config.upload) {
            const crp1ScanProjectJobAdapterId = this.jobAdapterService.getCrp1ScanProjectJobAdapter().ID;
            const isCRP1 = jobAdapter.getId() === crp1ScanProjectJobAdapterId;

            this.config.isProgressBarHidden = isCRP1;
            this.config.upload.uploadId = this.config.uploadId;
        }
        this.config.name = `${this.project.getName()} - ${this.translationService
            .translate(`SCANPROC.TASK_${this.task.name}`)
            .toLowerCase()}`;

        jobAdapter.setProjectVersion(this.workflowCore.getCurrentVersion());
        jobAdapter.setObjectType('projects');

        this.processWorkzonePanel(config);

        await this.workflowCore.uploadWorkspace(this.project);

        this.config.files.forEach((file: FileExtended) => {
            file.alias = this.filesHelperService.cleanUpFileNameCharacters(file.name);
        });

        if (this.isCreateStation()) {
            this.updateConfigForCreateStation(jobAdapter);
        }

        let uploadWkspDownloadProject: Promise<any> = Promise.resolve();

        if (this.isImportScan() || this.isImportStation() || this.isCreateStation()) {
            jobAdapter.enableWorkspaceProject();
            const workspaceDownloadProjectJson = { stations: [] };
            uploadWkspDownloadProject = this.dbPcvizService
                .getWorkspaceController()
                .uploadDownloadWorkspaceFile(workspaceDownloadProjectJson);
        } else if (this.isUpgradeSparseToFull()) {
            jobAdapter.setJobType(this.task.name);
        } else if (this.isImportRefs() && jobAdapter.getId() === 'BIM') {
            this.config.outputUUID = this.dbPcvizService.getUtils().generateUUID();
        }

        this.scanWorkflowJobData.failureCallback = this.task.name;

        if (uploadWkspDownloadProject) {
            try {
                await uploadWkspDownloadProject;
                this.upload();
            } catch (e: any) {
                this.logService.error('Error uploading creating workspace', e);
            }
        }
    }

    /**
     * Create the upload job promise to get the job number
     */
    async createJobPromise(): Promise<number | void> {
        if (this.resume) {
            return Promise.resolve(this.scanWorkflowJobData.processId);
        } else if (this.isImportStation() || this.isModelConverter()) {
            return this.jobAdapterService.uploadFilesByConfig(this.config);
        } else {
            return this.jobAdapterService.createAndUpload(this.config);
        }
    }

    /**
     * Upload the files based on the import type
     */
    async upload(): Promise<void> {
        const jobId = await this.createJobPromise();
        this.scanWorkflowJobData.processId = jobId;
        this.workflowScanService.addAvoidReloadScans(jobId);

        if (this.isSingleGeometryImportReferencesProcess()) {
            this.scanWorkflowJobData.newLayerID = this.config.object?.id;
            this.launchTask(); // cannot be launched to store config until we have the new layer ID

            // we need to add the topic notification now to get the job_started notification when uploading a pdf or fbx(OBJ)
            this.notificationService.addTopicNotification(`${NotificationTopics.LAYERS}${this.config.object?.id}`);
        }

        let uploadJsonPromise = Promise.resolve();
        // If the task is IMPORT PROJECT, IMPORT_STATION, CREATE_STATION or UPGRADE_SPARSE_TO_FULL, save the data needed to launch the process scans workflow
        const jobAdapter = this.jobAdapterService.getJobAdapter() as PcvizJobAdapter;

        if (this.isImportProject() || this.isImportStation() || this.isCreateStation() || this.isUpgradeSparseToFull()) {
            this.project.setExtensibleData({ jobOptions: (jobAdapter as any).getOptions() });
        } else if (this.isImportRefs() && jobAdapter.getId() === 'BIM') {
            // We need to upload necessary JSON file for verity-engine docker image.
            // This should be done here after the layer has been created and before the job is launched
            const json: UploadIfcImportFileParamJson = {
                CommandName: 'ImportModel',
                CommandGUID: this.config.outputUUID, // this should be the same sent in the job
                InputDirectory: '/app/fakedata/importmodel/', // or importmodelbuildtech `/data/${this.$scope.config.jobAdapter.getObjectType()}/${this.$scope.config.upload.object.id}/process/`,
                OutputDirectory: `/data/${this.config.jobAdapter?.getObjectType()}/${this.config.upload?.object?.id}/process/`,
                AttributeCollection: {},
            };
            const layerObj = {
                id: this.config.upload?.object?.id,
            };
            uploadJsonPromise = this.workflowCore.uploadIfcImportFile(layerObj, json);
        }

        await uploadJsonPromise;
        if (this.isImportStation()) {
            this.importStation(jobAdapter);
            return;
        }

        if (this.isModelConverter()) {
            this.runAndAttachModelConverter();
            return;
        }
        this.jobAdapterService.convertLayer(this.config).then(
            (process) => {
                if (this.isImportProject() || this.isImportScan()) {
                    this.workflowCore.setScanProjectFiles(JSON.parse(process.inputData).files);
                }

                this.closeWizardSubject$.next();

                if (!process) {
                    return;
                }

                const currentWorkflow = this.workflowScanService.workflow;
                (currentWorkflow as any).writeToProject(this.project);

                this.jobService.startCheckProcessStatus(process, this.config);
            },
            async (): Promise<void> => {
                if (this.config.upload?.status === 'INCOMPLETE') {
                    return;
                }
                await this.workflowScanService.showFailedTaskDialog(this.workflowScanService.workflow);
                this.isWorkflowUploading = false;
            }
        );

        this.launchTaskForMultipleGeometry();
    }

    /**
     * Launch task when an import is importing 3d models
     */
    launchTaskForMultipleGeometry(): void {
        if (
            this.isImportProject() ||
            this.isImportScan() ||
            this.isCreateStation() ||
            this.isUpgradeSparseToFull() ||
            this.isMultipleGeometryImportReferencesProcess()
        ) {
            this.launchTask();
        }
    }

    /**
     * Tells if the import is an project import
     */
    isImportProject(): boolean {
        return this.task.name === this.workflowCore.SCANTASK.IMPORT_PROJECT;
    }

    /**
     * Tells if the import is an scan import
     */
    isImportScan(): boolean {
        return this.task.name === this.workflowCore.SCANTASK.IMPORT_SCAN;
    }

    /**
     * Tells if the import is a station creation
     */
    isCreateStation(): boolean {
        return this.task.name === this.workflowCore.SCANTASK.CREATE_STATION;
    }

    /**
     * Tells if the import is an station import
     */
    isImportStation(): boolean {
        if (!this.task || !this.workflowCore) {
            return false;
        }
        return this.task.name === this.workflowCore.SCANTASK.IMPORT_STATION;
    }

    /**
     * Tells if the import is an upgrade sparse scan
     */
    isUpgradeSparseToFull(): boolean {
        return this.task.name === this.workflowCore.SCANTASK.UPGRADE_SPARSE_TO_FULL;
    }

    /**
     * Tells if the import is an references import
     */
    isImportRefs(): boolean {
        return this.task.name === this.workflowCore.SCANTASK.IMPORT_REFS;
    }

    /**
     * Tells if the import is an model converter process
     */
    isModelConverter(): boolean {
        return this.modelConverterService.isModel3dConversion();
    }

    /**
     * Uploads .sta and .alg files when creating a station
     * @param filePath Path file
     * @param fileName File name
     */
    createAndUploadFiles(filePath: string, fileName: string): void {
        // Project driver
        const projectDriver = this.project.getProjectDataset().getDriver();

        // creating sta file
        const staFileName = fileName + '.sta';
        const staFile = `NAME\nNO\n0.0\n0.0\n0.0\n0.0\n0.0\n0.0\n0.0\n`;

        projectDriver.uploadObjectFile(this.project, staFile, staFileName, filePath, 'application/json', 'no-cache', 'attachment');

        // creating alg file
        const algFileName = fileName + '.alg';

        const pointSelectorForm = this.pointSelectorService.pointSelectorForm;
        const algFile =
            'GLS-2000 ALG File\n' +
            `1 0 0 ${pointSelectorForm.get('x')?.value}\n` +
            `0 1 0 ${pointSelectorForm.get('y')?.value}\n` +
            `0 0 1 ${pointSelectorForm.get('z')?.value}\n` +
            '0 0 0 1\n';
        projectDriver.uploadObjectFile(this.project, algFile, algFileName, filePath, 'application/json', 'no-cache', 'attachment');
    }

    processWorkzonePanel(config: PcvizUploadConfig): void {
        if (!config.workspace) {
            return;
        }

        const jobAdapter = this.jobAdapterService.getJobAdapter() as PcvizJobAdapter;

        const workzoneConfig = this.workspaceService.workspaceForm;
        const newDatasetName = workzoneConfig.get('newDatasetName')?.value;
        if (this.isSingleGeometryImportReferencesProcess()) {
            jobAdapter.setObjectType('layers');
            config.object = { name: newDatasetName };
        } else {
            // HACK -> Review options
            if (Array.isArray(jobAdapter.getOptions())) {
                jobAdapter.getOptions().push({ name: newDatasetName });
            } else {
                (jobAdapter.getOptions() as any).name = newDatasetName;
            }
        }

        if (jobAdapter.getId() === 'DDU') {
            return;
        }

        // store destination workspace group element ID to add the imported layer when done
        const selectedGroup = workzoneConfig.get('selectedGroup')?.value;
        this.scanWorkflowJobData.destinationWksGroupID = selectedGroup ? selectedGroup.uuid : -1;

        // when the user creates a new station. Creating new prefix
        if (this.isCreateStation()) {
            this.newDatasetName = newDatasetName.replace(/\s/g, '_');
        }

        // store new workspace group category, if this option is selected
        const useExistingGroup = workzoneConfig.get('useExistingGroup')?.value;
        if (useExistingGroup) {
            this.computeImportPath(this.scanWorkflowJobData.destinationWksGroupID);
        }
    }

    computeImportPath(wksUUID: string): void {
        if (!this.isImportScan() && !this.isImportStation()) {
            return;
        }

        const wksDataCache = this.dbPcvizService.getControllerDB('Workspace').getWorkspaceDataCache();
        const wksObject = wksDataCache.getDataCacheObject(wksUUID).getWorkspaceObj();
        if (wksObject.path) {
            this.jobAdapterService.getJobAdapter()?.setWorkspaceImportPath(wksObject.path);
        }
    }

    /**
     * Checks if the file is a gis:cvs compliant, only when no importing references
     */
    isSingleGeometryImportReferencesProcess(): boolean {
        if (!this.isImportRefs()) {
            return false;
        }

        const jobAdapter = this.jobAdapterService.getJobAdapter();
        if (!jobAdapter) {
            return false;
        }
        return jobAdapter.getId() !== 'GISU' || jobAdapter.getDetectedType() === 'Gis:csv';
    }

    /**
     * Executes a task and updates the state machine
     */
    launchTask(): void {
        this.workflowCore
            .runTask(this.project, this.task.name)
            .then(() => {
                if (this.workflowScanService.isProgressState(this.workflowCore)) {
                    this.workflowCore.addJob(this.project, this.scanWorkflowJobData);
                    this.workflowScanService.resetCurrentProgress();
                    if (!this.workflowScanService.getProgressTimer()) {
                        this.workflowScanService.setupProgressTracking(this.workflowCore);
                    }
                }
            })
            .catch((e: any) => this.logService.error('Error launching the import task', e));

        this.logService.debug(`New scan project state = ${this.workflowCore.getState()}`);
    }

    /**
     * Checks if the import task is to import references and detected type is gis:dxf
     */
    isMultipleGeometryImportReferencesProcess(): boolean {
        if (!this.isImportRefs) {
            return false;
        }

        const jobAdapter = this.jobAdapterService.getJobAdapter() as PcvizJobAdapter;

        return jobAdapter.getId() === 'GISU' && jobAdapter.getDetectedType() !== 'Gis:csv';
    }

    /**
     * Set the configuration for the create station import
     */
    updateConfigForCreateStation(jobAdapter: PcvizJobAdapter): void {
        this.config.prefix = 'scan_project/';
        if (this.config.upload) {
            this.config.upload.useFilePath = true;
        }

        const filesArray: string[] = [];

        this.config.files.forEach((file: FileExtended) => {
            const name = this.filesHelperService.cleanUpFileNameCharacters(file.name);

            file.path = `${name}/pointclouds/${name}`;
            this.createAndUploadFiles(`process/${this.config.prefix}${name}/`, 'CW');
            filesArray.push(`process/${this.config.prefix}${name}/*`);
        });

        this.config.filesPath = `process/scan_project/${this.newDatasetName}/*`;
        jobAdapter?.setFilesArray(filesArray);
    }

    private async importStation(jobAdapter: PcvizJobAdapter): Promise<void> {
        const projectId = this.project.getId();
        const crp1ScanProjectJobAdapterId = this.jobAdapterService.getCrp1ScanProjectJobAdapter().ID;
        const isCRP1 = jobAdapter.getId() === crp1ScanProjectJobAdapterId;
        const preprocess = isCRP1
            ? this.compressFiles(jobAdapter as PcvizCRP1ScanProjectJobAdapter, projectId)
            : this.jobAdapterService.uploadFiles(this.config);
        await preprocess;
        try {
            const jopAdapterOptions = jobAdapter.getOptions() as PcvizScanProcessingOptions;
            const jobDataEndpoint: ImportStationJobData = {
                processingOptions: jopAdapterOptions,
                shouldProcessScans: jopAdapterOptions.processPanorama || jopAdapterOptions.processPointcloud,
            };

            if (isCRP1) {
                jobDataEndpoint.scans = (jobAdapter as PcvizCRP1ScanProjectJobAdapter).getScansData();
            } else {
                jobDataEndpoint.globalSource = `process/${this.config.files[0].name}`;
            }

            const projectDriver = this.project.getProjectDataset().getDriver();
            const jobResult = await projectDriver.launchWorkflowJob(projectId, jobDataEndpoint, 'import-and-process-station-data');

            this.closeWizardSubject$.next();

            this.scanWorkflowJobData.processes = jobResult.job.processes;
            this.scanWorkflowJobData.processId = jobResult.job.id;

            const currentWorkflow = this.workflowScanService.workflow;
            currentWorkflow.writeToProject(this.project);
            this.launchTask();
            this.jobService.startCheckProcessStatus(jobResult, this.config);
        } catch (e: unknown) {
            if (this.config.upload?.status === 'INCOMPLETE') {
                return;
            }
            await this.workflowScanService.showFailedTaskDialog(this.workflowScanService.workflow);
            this.isWorkflowUploading = false;
        }
    }

    private async runAndAttachModelConverter(): Promise<void> {
        await this.jobAdapterService.uploadFiles(this.config);
        try {
            const jobResult = await this.modelConverterService.callModelConverterEndpoint(this.config);

            this.closeWizardSubject$.next();

            this.scanWorkflowJobData.processId = jobResult.id;

            const currentWorkflow = this.workflowScanService.workflow;
            currentWorkflow.writeToProject(this.project);

            this.jobService.startCheckProcessStatus(jobResult, this.config);
        } catch {
            if (this.config.upload?.status === 'INCOMPLETE') {
                return;
            }
            await this.workflowScanService.showFailedTaskDialog(this.workflowScanService.workflow);
            this.isWorkflowUploading = false;
        }
    }

    private async compressFiles(jobAdapter: PcvizCRP1ScanProjectJobAdapter, projectId: number): Promise<void> {
        return new Promise((resolve, reject) => {
            const filesInfo = this.config.upload?.filesInfo ?? [];

            const subscriptionWrapper: { subscription?: SubscriptionHandler } = {};
            subscriptionWrapper.subscription = this.crp1ImportSdService.onCompressedResponse((event: Event) => {
                subscriptionWrapper?.subscription?.cancel();
                if (!event?.message?.success) {
                    filesInfo.length = 0;
                    reject('Error compressing files');
                    return;
                }
                filesInfo.forEach((file) => {
                    file.progress = MAX_PROGRESS;
                });
                resolve();
            });
            filesInfo.forEach((file) => {
                file.progress = HALF_PROGRESS;
                file.isIndeterminate = true;
            });
            this.crp1ImportSdService.copyAndCompressFiles(jobAdapter.getDstSrcFilePathsFor(projectId));
        });
    }
}
